import { Connector } from "@web3-react/types";
import {
  coinbaseWalletConnection,
  Connection,
  ConnectionType,
  gnosisSafeConnection,
  injectedConnection,
  walletConnectConnection,
} from "./connectors";
import { WalletKey } from "@silo/types";
import { match } from "ts-pattern";

export type SupportedWallet = Connection & {
  name: string;
  icon: string;
};

export interface WalletLink {
  name: string;
  icon: string;
  href: string;
}

export const metaMaskWallet: SupportedWallet = {
  name: "MetaMask",
  icon: "metamask.png",
  ...injectedConnection,
};

export const coinbaseWallet: SupportedWallet = {
  name: "Coinbase Wallet",
  icon: "coinbase.svg",
  ...coinbaseWalletConnection,
};

export const walletConnectWallet: SupportedWallet = {
  name: "WalletConnect",
  icon: "wallet-connect.svg",
  ...walletConnectConnection,
};

export const gnosisSafeWallet: SupportedWallet = {
  name: "Safe",
  icon: "gnosis.png",
  ...gnosisSafeConnection,
};

export const supportedWallets = [
  metaMaskWallet,
  coinbaseWallet,
  walletConnectWallet,
  gnosisSafeWallet,
];

export const metaMaskLink: WalletLink = {
  name: "Install Metamask",
  icon: "metamask.png",
  href: "https://metamask.io/",
};

export const coinbaseLink: WalletLink = {
  name: "Open in Coinbase Wallet",
  icon: "coinbase.svg",
  href: "https://go.cb-w.com/mtUDhEZPy1",
};

export type ConnectWallet = (params: {
  wallet: SupportedWallet;
  previousWallet?: SupportedWallet;
}) => Promise<void>;

export const connectWallet: ConnectWallet = async ({
  wallet,
  previousWallet,
}) => {
  await wallet.connector.activate();

  // disconnect previous wallet, as web3react may prioritize the incorrect wallet
  disconnect(previousWallet?.connector);
};

export const disconnect = (connector?: Connector) => {
  if (connector?.deactivate) {
    connector.deactivate();
  } else {
    connector?.resetState();
  }
};

export function getWalletKeyFromSupportedWallet(
  wallet: SupportedWallet
): WalletKey {
  return match(wallet.type)
    .returnType<WalletKey>()
    .with(ConnectionType.COINBASE_WALLET, () => "coinbase")
    .with(ConnectionType.GNOSIS_SAFE, () => "safe")
    .with(ConnectionType.INJECTED, () => "metaMask")
    .with(ConnectionType.WALLET_CONNECT, () => "walletConnect")
    .otherwise(() => "injected");
}

export function getSupportedWalletFromWalletKey(
  walletKey: WalletKey
): SupportedWallet {
  return match(walletKey)
    .returnType<SupportedWallet>()
    .with("coinbase", () => coinbaseWallet)
    .with("metaMask", () => metaMaskWallet)
    .with("safe", () => gnosisSafeWallet)
    .with("walletConnect", () => walletConnectWallet)
    .otherwise(() => metaMaskWallet);
}
